import { Box } from "@chakra-ui/react";
import { NextSeo, ArticleJsonLd } from "next-seo";
import { GetServerSideProps } from "next";
import { ApolloClient, InMemoryCache } from "@apollo/client";
import { GET_AUTHOR_ARTICLES_TOTAL, GET_BREED, GET_BREED_RATING, GET_FOOTER_DATA, GET_HEADER_DATA, GET_SIMILAR_BREED_BEHAVIOR_ATTACHEDTOTHEOWNER, GET_SIMILAR_BREED_BEHAVIOR_LIVEINAPARTMENT } from "@/graphql/queries";
import { handleError } from "@/helpers/handleError";
import { BreadcrumbType } from "@/Types/types";
import { breadcrumbInitial } from "@/data/breadcrumbInitial";
import axios from "axios";

import HeaderMenu from "@/components/HeaderMenu";
import Footer from "@/components/Footer";
import BreedBanner from "@/components/BreedPage/BreedBanner";
import Characteristics from "@/components/BreedPage/Characteristics";
import Content from "@/components/BreedPage/Content";
import { useEffect, useState } from "react";

export default function BreedPage(props: any) {
  const [hasReview, setHasReview] = useState<Boolean>(false);

  useEffect(() => {
    props.breedRating.data.length >= 1 ? setHasReview(true) : setHasReview(false);

  }, [props.breedRating])

  return (
    <Box bgColor="#FFFCF6">
      <HeaderMenu cookies={props.cookies.data} menu={props.header[0].items} submenu={props.navbar.data?.attributes.submenu_header} />
      <NextSeo
        title={props.breed.data[0].attributes.seo.metaTitle}
        description={props.breed.data[0].attributes.seo.metaDescription}
        canonical={props.breed.data[0].attributes.seo.canonicalURL}
        openGraph={{
          url: `${process.env.NEXT_PUBLIC_SITE_URL}raca/${props.breed.data[0].attributes.url}`,
          title: props.breed.data[0].attributes.name,
          description: props.breed.data[0].attributes.previewText,
          type: 'article',
          article: {
            authors: [
              props.breed.data[0].attributes.author.data.attributes.name
            ]
          },
          images: [
            {
              url: props.breed.data[0].attributes.image.data.attributes.url,
              alt: props.breed.data[0].attributes.name
            }
          ]
        }}
      />
      <ArticleJsonLd
        url={`${process.env.NEXT_PUBLIC_SITE_URL}raca/${props.breed.data[0].attributes.url}`}
        title={props.breed.data[0].attributes.name}
        description={props.breed.data[0].attributes.previewText}
        images={[props.breed.data[0].attributes.image.data.attributes.url]}
        datePublished={props.breed.data[0].attributes.image.data.attributes.updatedAt}
        authorName={[
          {
            name:
              props.breed.data[0].attributes.author.data &&
              props.breed.data[0].attributes.author.data.attributes.name,
            url:
              props.breed.data[0].attributes.author.data &&
              `${process.env.NEXT_PUBLIC_SITE_URL}blog/perfil/${props.breed.data[0].attributes.author.data.attributes.url}`,
          },
        ]}
        additionalProperty={[
          {
            type: "PropertyValue",
            name: "Peso",
            value: `${props.breed.data[0].attributes.minWeight}-${props.breed.data[0].attributes.maxWeight}`,
            unitText: "kg"
          },
          {
            type: "PropertyValue",
            name: "Expectativa de Vida",
            value: `${props.breed.data[0].attributes.lifeExpectancyInYears} anos`
          },
          {
            type: "PropertyValue",
            name: "Convívio em apartamento",
            value: `${props.breed.data[0].attributes.rating.liveInApartment}`,
            minValue: 1,
            maxValue: 5
          },
          {
            type: "PropertyValue",
            name: "Apego com o dono",
            value: `${props.breed.data[0].attributes.rating.attachedToTheOwner}`,
            minValue: 1,
            maxValue: 5
          },
          {
            type: "PropertyValue",
            name: "Tendência de latir",
            value: `${props.breed.data[0].attributes.rating.barkTendency}`,
            minValue: 1,
            maxValue: 5
          },
          {
            type: "PropertyValue",
            name: "Facilidade para adestrar",
            value: `${props.breed.data[0].attributes.rating.easeToTrain}`,
            minValue: 1,
            maxValue: 5
          },
          {
            type: "PropertyValue",
            name: "Convívio com crianças",
            value: `${props.breed.data[0].attributes.rating.socializingWithChildren}`,
            minValue: 1,
            maxValue: 5
          },
          {
            type: "PropertyValue",
            name: "Convívio com outros animais",
            value: `${props.breed.data[0].attributes.rating.coexistenceWithOtherAnimals}`,
            minValue: 1,
            maxValue: 5
          },
          {
            type: "PropertyValue",
            name: "Queda de pelos",
            value: `${props.breed.data[0].attributes.rating.hairLoss}`,
            minValue: 1,
            maxValue: 5
          }
        ]}
        isAccessibleForFree={true}
      />
      <BreedBanner data={props.breed.data[0]} breadcrumbs={props.breadcrumbs} />
      <Characteristics data={props.breed.data[0]} hasReview={hasReview} />
      <Content data={props.breed} authorArticlesTotal={props.authorArticlesTotal} comments={props.comments} breedRating={props.breedRating} similarBehaviors={props.filterSimilarBehaviors} />
      <Footer menu={props.footer[0].items} />
    </Box>
  )

}

export const getServerSideProps: GetServerSideProps = async (context) => {
  try {
    context.res.setHeader(
      "Cache-Control",
      "public, s-maxage=10, stale-while-revalidate=59"
    );

    const client = new ApolloClient({
      uri: `${process.env.NEXT_PUBLIC_API_URL}/graphql`,
      cache: new InMemoryCache(),
    });

    const { data: headerData } = await client.query({
      query: GET_HEADER_DATA,
    });

    const { data: footerData } = await client.query({
      query: GET_FOOTER_DATA,
    });

    const { data: breedData } = await client.query({
      query: GET_BREED,
      variables: {
        slugUrl: context.params?.slug,
      },
    });

    if (!breedData.breed.data.length) {
      return {
        notFound: true,
      };
    }

    const breadcrumbs: BreadcrumbType[] = JSON.parse(JSON.stringify(breadcrumbInitial));

    breadcrumbs.push({
      href: `/raca`,
      label: `Raças`,
    })

    // let animalType: { title: string; url: string } | undefined

    // if (breedData.breed.data[0].attributes.animalType === "cachorro") {
    //   animalType = { title: "Raças de Cães", url: "/cachorro" }
    // } else if (breedData.breed.data[0].attributes.animalType === "gato") {
    //   animalType = { title: "Raças de Gatos", url: "/gato" }
    // }

    // if (animalType) {
    //   breadcrumbs.push({
    //     href: animalType.url,
    //     label: animalType.title,
    //   });
    // }

    breadcrumbs.push({
      href: `${breedData.breed.data[0].attributes.url}`,
      label: `${breedData.breed.data[0].attributes.name}`,
    })

    const {
      data: {
        articles: {
          meta: {
            pagination: { total: authorArticlesTotal },
          },
        },
      },
    } = await client.query({
      query: GET_AUTHOR_ARTICLES_TOTAL,
      variables: { authorId: breedData.breed.data[0].attributes.author.data.id },
    });

    const comments = await axios.get(
      `${process.env.NEXT_PUBLIC_API_URL}/api/comments/api::breed.breed:${breedData.breed.data[0].id}?filters[approvalStatus][$eq]=APPROVED&filters[blocked][$ne]=true`
    );

    const { data: breedRatings } = await client.query({
      query: GET_BREED_RATING,
      variables: {
        slugUrl: context.params?.slug,
      },
    });

    const { data: similarBehaviorsliveInApartment } = await client.query({
      query: GET_SIMILAR_BREED_BEHAVIOR_LIVEINAPARTMENT,
      variables: {
        rating: breedData.breed.data[0].attributes.rating.liveInApartment
      }
    })

    const { data: similarBehaviorsAttachedToTheOwner } = await client.query({
      query: GET_SIMILAR_BREED_BEHAVIOR_ATTACHEDTOTHEOWNER,
      variables: {
        rating: breedData.breed.data[0].attributes.rating.attachedToTheOwner
      }
    })

    const unifySimilarBehaviors = [
      similarBehaviorsliveInApartment.similarBreedLiveInApartment.data
      ,
      similarBehaviorsAttachedToTheOwner.similarBreedAttachedToTheOwner.data
    ]

    const flatSimilarBehaviors = unifySimilarBehaviors.flat()

    const filterSimilarBehaviors = flatSimilarBehaviors.filter((item, index, self) => {
      return index === self.findIndex((t) => (
        t.id === item.id
      ));
    });

    return {
      props: {
        ...headerData,
        ...footerData,
        ...breedData,
        ...breedRatings,
        filterSimilarBehaviors,
        breadcrumbs,
        authorArticlesTotal,
        comments: comments.data
      },
    };
  } catch (error) {
    handleError(error, "Raça Slug getServerSideProps");
    return {
      notFound: true,
    };
  }
};